import { useEffect, useMemo, useState } from 'react'

import { ComboboxItem, Loader, Select, SelectProps } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { useTranslate } from 'react-admin'
import { YoutubeVideosAttributesApiDTO } from '../codegen/generated/Api.schemas'
import { useGetApiVideoPromotionVideos } from '../codegen/generated/video-promotion'
import { YoutubeChannel } from '../types/YoutubeChannel'

const PAGE_SIZE = 20
const MORE_RESULTS_VALUE = '__more_results__'

type LXVideoSelectProps = Omit<
  SelectProps,
  'data' | 'value' | 'onChange' | 'searchable' | 'searchValue' | 'onSearchChange' | 'filter'
> & {
  youtubeChannelId: YoutubeChannel['id'] | undefined
  value: YoutubeVideosAttributesApiDTO['id'] | undefined
  onChange: (video: YoutubeVideosAttributesApiDTO | undefined) => void
}

export const LXVideoSelect = ({
  youtubeChannelId,
  value,
  onChange,
  disabled,
  ...selectProps
}: LXVideoSelectProps) => {
  const t = useTranslate()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [pickedVideo, setPickedVideo] = useState<YoutubeVideosAttributesApiDTO>()

  const selectedVideoQuery = useGetApiVideoPromotionVideos(
    { youtubeChannelId: youtubeChannelId ?? '', videoId: value, pageSize: 1 },
    { query: { enabled: !!youtubeChannelId && !!value } },
  )
  const selectedVideo =
    selectedVideoQuery.data?.body.videos[0] ?? (pickedVideo?.id === value ? pickedVideo : undefined)

  useEffect(() => {
    setSearch(selectedVideo?.title ?? '')
  }, [selectedVideo?.title])

  const searchTerm = search === selectedVideo?.title ? '' : debouncedSearch
  const videosQuery = useGetApiVideoPromotionVideos(
    {
      youtubeChannelId: youtubeChannelId ?? '',
      search: searchTerm || undefined,
      pageSize: PAGE_SIZE,
    },
    { query: { enabled: !!youtubeChannelId, keepPreviousData: true } },
  )

  const videos = useMemo(() => {
    const results = videosQuery.data?.body.videos ?? []
    if (selectedVideo && !results.some(v => v.id === selectedVideo.id)) {
      return [selectedVideo, ...results]
    }
    return results
  }, [videosQuery.data, selectedVideo])

  const data = useMemo<ComboboxItem[]>(() => {
    const options: ComboboxItem[] = videos.map(v => ({ value: v.id, label: v.title }))
    const total = videosQuery.data?.body.total ?? 0
    if (total > PAGE_SIZE) {
      options.push({
        value: MORE_RESULTS_VALUE,
        label: t('_generics.refineSearch', { smart_count: total }),
        disabled: true,
      })
    }
    return options
  }, [videos, videosQuery.data, t])

  return (
    <Select
      {...selectProps}
      data={data}
      value={value ?? null}
      onChange={id => {
        const video = videos.find(v => v.id === id)
        setPickedVideo(video)
        onChange(video)
      }}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      filter={({ options }) => options}
      nothingFoundMessage={videosQuery.isFetching ? undefined : t('_generics.noResult')}
      rightSection={videosQuery.isFetching ? <Loader size="xs" /> : undefined}
      disabled={disabled || !youtubeChannelId}
    />
  )
}
